/**
 * Utility functions for location and address handling
 */

/**
 * Generate Google Maps URL without API key
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate  
 * @param {string} address - Address string
 * @param {string} name - Name string
 * @returns {string} Google Maps URL
 */
export const generateGoogleMapsUrl = (latitude, longitude, address, name = null) => {
    if (latitude && longitude) {
        // Use coordinates if available - most accurate
        return `https://www.google.com/maps?q=${latitude},${longitude}`;
    } else if (address) {
        // Use address if coordinates not available
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    } else if (name) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;
    }
    return '#';
};

/**
 * Extract location data from doctor object
 * @param {Object} doctor - Doctor object
 * @returns {Object} Location data with lat, lng, address, full_address
 */
export const extractDoctorLocation = (doctor) => {
    const locationData = doctor?.doctor_default_location && doctor.doctor_default_location.length > 0 
        ? doctor.doctor_default_location[0] 
        : null;
    
    return {
        latitude: locationData?.lat || null,
        longitude: locationData?.lng || null,
        address: locationData?.address || doctor.address || null,
        fullAddress: locationData?.full_address || locationData?.address || doctor.address || null
    };
};

/**
 * Extract address string from doctor location data (for MapComponent)
 * @param {Object} doctor - Doctor object
 * @param {Object} fallbackData - Fallback data object with address
 * @returns {string} Address string
 */
export const extractAddressForMap = (doctor, fallbackData = null) => {
    if (doctor?.doctor_default_location && doctor.doctor_default_location.length > 0) {
        const location = doctor.doctor_default_location[0];
        return location.full_address || location.address || '';
    }
    return fallbackData?.address || '';
};

/**
 * Format address for display with fallback options
 * @param {Object} locationData - Location data object
 * @param {number} maxLength - Maximum length for truncated display
 * @returns {string} Formatted address
 */
export const formatAddressForDisplay = (locationData, maxLength = 50) => {
    const { fullAddress, address } = locationData;
    
    // Prefer full address, fallback to regular address
    const displayAddress = fullAddress || address || 'Location not available';
    
    if (displayAddress.length <= maxLength) {
        return displayAddress;
    }
    
    // Truncate intelligently at comma or space
    const truncated = displayAddress.substring(0, maxLength);
    const lastComma = truncated.lastIndexOf(',');
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastComma > lastSpace && lastComma > maxLength * 0.7) {
        return displayAddress.substring(0, lastComma) + '...';
    } else if (lastSpace > maxLength * 0.7) {
        return displayAddress.substring(0, lastSpace) + '...';
    }
    
    return truncated + '...';
};

/**
 * Open Google Maps in new tab
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @param {string} address - Address string
 */
export const openGoogleMaps = (latitude, longitude, address) => {
    const url = generateGoogleMapsUrl(latitude, longitude, address);
    if (url !== '#') {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
};
